import { useState } from 'react';
import { Check, RotateCcw, SlidersHorizontal, X } from 'lucide-react';
import { devPalettePresets } from './presets';
import { fontPresets, spacingOptions, radiusOptions, type ScaleOption } from './axes';
import {
  applyDevTheme,
  readDevTheme,
  resetDevTheme,
  storeDevTheme,
  DEFAULT_THEME,
  type DevThemeState,
} from './apply';
import { DevPaletteSwatches } from './swatches';

// Dev-only design playground — bottom-right. Compares the redesign direction
// live across four axes (color, type, density, radius) by writing CSS custom
// properties onto <html>. Guarded by import.meta.env.DEV; never ships to prod.
export function DevPaletteBox() {
  if (!import.meta.env.DEV) return null;
  return <DevPaletteBoxInner />;
}

function DevPaletteBoxInner() {
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState<DevThemeState>(() => readDevTheme());

  function update(patch: Partial<DevThemeState>) {
    setTheme((prev) => {
      const next = { ...prev, ...patch };
      applyDevTheme(next);
      storeDevTheme(next);
      return next;
    });
  }

  function reset() {
    resetDevTheme();
    setTheme({ ...DEFAULT_THEME });
  }

  const isDefault =
    theme.palette === DEFAULT_THEME.palette &&
    theme.font === DEFAULT_THEME.font &&
    theme.spacing === DEFAULT_THEME.spacing &&
    theme.radius === DEFAULT_THEME.radius;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-3 z-50 flex h-9 w-9 items-center justify-center rounded-md border bg-card text-muted-foreground shadow-lg transition-colors hover:text-foreground md:bottom-14"
        title="Design playground (dev)"
      >
        <SlidersHorizontal className="h-4 w-4" />
        {!isDefault && (
          <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-primary" />
        )}
      </button>
    );
  }

  return (
    <div className="fixed bottom-20 right-3 z-50 flex max-h-[85vh] w-[19rem] flex-col overflow-hidden rounded-md border bg-card shadow-xl md:bottom-14">
      <header className="flex shrink-0 items-center justify-between border-b px-3 py-2">
        <span className="flex items-center gap-1.5 text-xs font-semibold tracking-wide">
          <SlidersHorizontal className="h-3.5 w-3.5" /> Design playground
          <span className="rounded-sm bg-muted px-1 text-[10px] font-medium text-muted-foreground">
            dev
          </span>
        </span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-muted-foreground hover:text-foreground"
          title="Đóng"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </header>

      <div className="flex flex-col gap-4 overflow-y-auto px-3 py-3">
        {/* Color */}
        <Section label="Bảng màu">
          <div className="flex flex-col gap-1">
            {devPalettePresets.map((preset) => {
              const active = preset.id === theme.palette;
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => update({ palette: active ? null : preset.id })}
                  className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent"
                  style={
                    active
                      ? { outline: `2px solid ${preset.swatch.brand}`, outlineOffset: '-2px' }
                      : undefined
                  }
                >
                  <DevPaletteSwatches swatch={preset.swatch} />
                  <span className="flex min-w-0 flex-1 flex-col leading-tight">
                    <span className="text-[13px] font-medium">{preset.name}</span>
                    <span className="truncate text-[11px] text-muted-foreground">
                      {preset.vibe}
                    </span>
                  </span>
                  {active && (
                    <Check className="h-3.5 w-3.5 shrink-0" style={{ color: preset.swatch.brand }} />
                  )}
                </button>
              );
            })}
          </div>
        </Section>

        {/* Typography */}
        <Section label="Font chữ">
          <div className="flex flex-col gap-1">
            {fontPresets.map((f) => {
              const active = f.id === theme.font;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => update({ font: f.id })}
                  className={`flex items-center gap-2.5 rounded-md border px-2.5 py-1.5 text-left transition-colors hover:bg-accent ${
                    active ? 'border-primary bg-accent' : 'border-transparent'
                  }`}
                >
                  <span
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-sm border bg-background text-base font-semibold"
                    style={{ fontFamily: f.display }}
                  >
                    Aa
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col leading-tight">
                    <span className="text-[13px] font-medium" style={{ fontFamily: f.sans }}>
                      {f.name}
                    </span>
                    <span className="truncate text-[11px] text-muted-foreground">{f.vibe}</span>
                  </span>
                  {active && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
                </button>
              );
            })}
          </div>
        </Section>

        {/* Density */}
        <Section label="Khoảng cách">
          <SegmentedScale
            options={spacingOptions}
            value={theme.spacing}
            onChange={(id) => update({ spacing: id })}
          />
        </Section>

        {/* Radius */}
        <Section label="Bo góc">
          <SegmentedScale
            options={radiusOptions}
            value={theme.radius}
            onChange={(id) => update({ radius: id })}
            preview="radius"
          />
        </Section>
      </div>

      <footer className="flex shrink-0 items-center justify-between border-t px-3 py-2">
        <button
          type="button"
          onClick={reset}
          disabled={isDefault}
          className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
        >
          <RotateCcw className="h-3 w-3" /> Về mặc định
        </button>
        <span className="text-[10px] text-muted-foreground">Chỉ hiện khi dev</span>
      </footer>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-1.5">
      <h3 className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </h3>
      {children}
    </section>
  );
}

function SegmentedScale({
  options,
  value,
  onChange,
  preview,
}: {
  options: ScaleOption[];
  value: string;
  onChange: (id: string) => void;
  preview?: 'radius';
}) {
  const active = options.find((o) => o.id === value) ?? options[0]!;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="grid grid-cols-4 gap-1 rounded-md bg-muted p-1">
        {options.map((o) => {
          const on = o.id === value;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => onChange(o.id)}
              className={`flex flex-col items-center gap-1 rounded-sm px-1 py-1.5 text-[11px] font-medium transition-colors ${
                on
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {preview === 'radius' ? (
                <span
                  className="h-4 w-4 border border-current"
                  style={{ borderRadius: o.value }}
                />
              ) : (
                <span
                  className="border border-current"
                  style={{ width: '1rem', height: `calc(${o.value} * 3)` }}
                />
              )}
              {o.name}
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground">{active.hint}</p>
    </div>
  );
}
